import { useApiQuery } from "../../../services/api";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";

/**
 * Whether the reader belongs to the room, from their room list. `pending`
 * while that list loads, for a caller that must not act on an unknown answer.
 *
 * The one place that answers it for the Join affordances: `useCanShowJoin`
 * hides the button, and the room offers hide the whole block. The list is the
 * query `useJoinRoom` revalidates after a join, so both update together.
 *
 * `isAuthenticated` from `useCurrentUser`, never `useFirebaseUser`: the latter
 * is not context-backed, so each call mounts its own auth observer.
 */
export function useRoomMembership(
  roomId: string | null
): "pending" | "member" | "non-member" {
  const { isAuthenticated } = useCurrentUser();
  const roomsQuery = useApiQuery(
    "/webapp/private/rooms",
    isAuthenticated ? { durable: true } : false
  );
  if (roomId === null) return "non-member";
  switch (roomsQuery.state) {
    case "idle":
    case "error":
      return "non-member";
    case "pending":
      return "pending";
    case "success":
      return roomsQuery.body.rooms.some((r) => r.id === roomId)
        ? "member"
        : "non-member";
    default:
      throw new Error(roomsQuery satisfies never);
  }
}

/** `useRoomMembership`, read as a plain answer: unknown counts as no. */
export function useIsRoomMember(roomId: string | null): boolean {
  return useRoomMembership(roomId) === "member";
}
